// Daily-View "Dropped / Unfilled" panel: one row per order × SKU the model
// could not fill. Searchable (customer, AU#, SKU), sortable by any column,
// and — when the host passes `onFocusOrder` — each row highlights its order
// on the Sankey canvas (the standalone page routes this through the same
// filter-chip path the search bar uses, so the chart and the list stay in
// sync and the chip strip shows what is highlighted).

import { useEffect, useMemo, useRef, useState } from 'react';
import { trackEventNow } from '../../../services/portalTracking';
import { unfilledOrderRows } from './mppHelpers';
import type { MPPRow } from './types';
import {
  DEFAULT_UNFILLED_SORT,
  filterUnfilledRows,
  nextUnfilledSort,
  sortUnfilledRows,
  unfilledTotals,
  type UnfilledSort,
  type UnfilledSortKey,
} from './unfilledSearch';
import styles from './DailyDashboard.module.css';
import up from './UnfilledPanel.module.css';

const formatLbs = (n: number) => Math.round(n).toLocaleString();

/** Debounce for the search audit row — one row per settled query, not per key. */
const SEARCH_TRACK_DEBOUNCE_MS = 600;

const COLUMNS: { key: UnfilledSortKey; label: string; numeric?: boolean }[] = [
  { key: 'order', label: 'Order' },
  { key: 'sku', label: 'SKU' },
  { key: 'lbs', label: 'Unfilled lbs', numeric: true },
];

export function UnfilledPanel({
  rows,
  activeModel,
  onExpand,
  fill = false,
  onFocusOrder,
  focusedOrders,
}: {
  rows: readonly MPPRow[];
  activeModel: string;
  onExpand?: () => void;
  /** Stretch the card + scroll area to fill the container (e.g. FloatingPanel). */
  fill?: boolean;
  /** Row click → highlight that order on the chart. Rows are inert when absent. */
  onFocusOrder?: (order: string) => void;
  /** Orders currently highlighted on the chart (rendered with a marker). */
  focusedOrders?: ReadonlySet<string>;
}) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<UnfilledSort>(DEFAULT_UNFILLED_SORT);

  const all = useMemo(
    () => unfilledOrderRows(rows, activeModel),
    [rows, activeModel],
  );
  const matched = useMemo(() => filterUnfilledRows(all, query), [all, query]);
  const data = useMemo(() => sortUnfilledRows(matched, sort), [matched, sort]);
  const totals = useMemo(() => unfilledTotals(all), [all]);
  const shown = useMemo(() => unfilledTotals(matched), [matched]);
  const filtering = matched !== all;

  // Audit row for the search box: the query and how many lines it left, once
  // the user stops typing. `recorded_by_dom` keeps the shared recorder (where
  // it is loaded) from logging the same settled query twice.
  const lastTracked = useRef('');
  useEffect(() => {
    const q = query.trim();
    if (q === lastTracked.current) return;
    const t = setTimeout(() => {
      lastTracked.current = q;
      trackEventNow('search', 'cplex_unfilled_search', {
        query: q,
        result_lines: shown.lines,
        result_orders: shown.orders,
        model: activeModel,
        recorded_by_dom: true,
      });
    }, SEARCH_TRACK_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, shown.lines, shown.orders, activeModel]);

  const onSort = (key: UnfilledSortKey) => {
    const next = nextUnfilledSort(sort, key);
    setSort(next);
    trackEventNow('filter', 'cplex_unfilled_sort', {
      sort: next.key,
      dir: next.dir,
      model: activeModel,
    });
  };

  const onRow = (order: string, sku: string) => {
    if (!onFocusOrder) return;
    trackEventNow('click', 'cplex_unfilled_order_focus', {
      order,
      sku,
      model: activeModel,
      outcome: focusedOrders?.has(order) ? 'unfocus' : 'focus',
    });
    onFocusOrder(order);
  };

  const clickable = onFocusOrder != null;

  return (
    <div className={`${styles.card} ${fill ? styles.cardFill : ''}`}>
      <div className={styles.cardHd}>
        <div>
          <div className={styles.cardTitle}>✗ Dropped / Unfilled</div>
          <div className={styles.cardSub}>
            Orders the model couldn&apos;t fill within the 5-day horizon
            {clickable ? ' · click a row to highlight it on the chart' : ''}
          </div>
        </div>
        <div className={styles.cardHdActions}>
          <span
            className={`${styles.pill} ${styles.pillBad}`}
            data-testid="unfilled-count-pill"
          >
            {filtering
              ? `${shown.orders} of ${totals.orders} orders`
              : `${totals.orders} orders`}
          </span>
          {onExpand && (
            <button
              type="button"
              className={styles.expandBtn}
              onClick={onExpand}
              title="Expand"
            >
              ⤢
            </button>
          )}
        </div>
      </div>

      <div className={up.searchRow}>
        <label className={up.searchBox}>
          <span className={up.searchIcon} aria-hidden="true">
            ⌕
          </span>
          <input
            type="search"
            className={up.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by customer, AU#, or SKU…"
            aria-label="Filter dropped orders"
            data-action="cplex_unfilled_search"
            spellCheck={false}
          />
          {query !== '' && (
            <button
              type="button"
              className={up.clearBtn}
              onClick={() => setQuery('')}
              title="Clear filter"
              aria-label="Clear filter"
              data-action="cplex_unfilled_search_clear"
            >
              ✕
            </button>
          )}
        </label>
        <span className={up.summary} data-testid="unfilled-summary">
          {shown.lines} {shown.lines === 1 ? 'line' : 'lines'} ·{' '}
          {formatLbs(shown.lbs)} lbs
          {filtering ? ` of ${formatLbs(totals.lbs)}` : ''}
        </span>
      </div>

      <div className={`${styles.scrollArea} ${fill ? '' : up.scrollTall}`}>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              {COLUMNS.map((c) => {
                const active = sort.key === c.key;
                return (
                  <th key={c.key} className={c.numeric ? up.num : undefined}>
                    <button
                      type="button"
                      className={`${up.sortBtn} ${active ? up.sortActive : ''}`}
                      onClick={() => onSort(c.key)}
                      data-action="cplex_unfilled_sort"
                      data-record-value="sort"
                      value={c.key}
                      aria-sort={
                        active
                          ? sort.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      aria-label={`Sort by ${c.label}`}
                      title={`Sort by ${c.label}`}
                    >
                      {c.label}
                      <span className={up.sortArrow} aria-hidden="true">
                        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((r) => {
              const focused = focusedOrders?.has(r.order) ?? false;
              return (
                <tr
                  key={`${r.order}|${r.sku}`}
                  className={`${clickable ? up.rowClickable : ''} ${
                    focused ? up.rowFocused : ''
                  }`}
                  onClick={clickable ? () => onRow(r.order, r.sku) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRow(r.order, r.sku);
                          }
                        }
                      : undefined
                  }
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  aria-pressed={clickable ? focused : undefined}
                  title={
                    clickable
                      ? focused
                        ? 'Remove highlight from the chart'
                        : 'Highlight this order on the chart'
                      : undefined
                  }
                  data-action={clickable ? 'cplex_unfilled_order_focus' : undefined}
                  data-target-order-id={clickable ? r.order : undefined}
                >
                  <td title={r.order}>
                    {r.order}
                    {focused && (
                      <span className={up.focusMark} title="Highlighted on chart">
                        ●
                      </span>
                    )}
                  </td>
                  <td title={r.sku}>{r.sku}</td>
                  <td className={up.num}>{formatLbs(r.lbs)}</td>
                </tr>
              );
            })}
            {data.length === 0 && (
              <tr>
                <td colSpan={3} className={up.empty}>
                  {all.length === 0
                    ? 'No dropped orders — every order was filled.'
                    : `No dropped orders match “${query.trim()}”.`}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
